import React from 'react';
import { useAccountStore } from '../../../../../components/Stores/accountStore';
import { OptionSelect } from '../../../../../components/UI-Elements/Select/OptionSelect';
import { ToggleNumberInput } from '../../../../../components/UI-Elements/ToogleNumber/ToggleNumberInput';
import { Material } from '../../../../../sp-types';
import { IClientExposure, IFutureOrder, ISupplierExposure } from '../../../../../sp-types/FutureOrderType';
import { formatToShortString } from '../../../../../utils/FormatValue';
import { MonthYearValue } from '../../../../../utils/MonthYear/MonthYearValue';
import { accumulateExposures, calcSupplierMinusClient } from '../../CreateOrderSummary/OrderSummaryUtils';
import { RAW_MATERIALS_PRICE_ADJUSTMENT_DICTIONARY } from '../RawMaterialsUtils';
import './ProductMaterialTable.css'; // Importing CSS file
import { getUnitObj } from '../../../../../utils/MassUnits/massUnitsUtils';
import { useCurrencyRates } from '@hooks/useCurrencyRates';

interface MaterialData {
  material: string;
  formulaId: string;
  materialColor: string;
  quantity: number;
  basePrice: number;
  weightInProduct: number;
  totalPrice: number;
  leadTime: { months: number; setMonthsValue: (_months: number) => void };
  priceIndex: string;
  adjustment: { adjustmentType: string; options: string[]; setAdjustment: (_option: string) => void };
}
const defaultAdjustment = 'Trailing month';
interface EditFutureOrderTableProps {
  materialsMap: Record<string, Material>;
  futureOrderData: {
    editedFutureOrder: IFutureOrder;
    updateSupplierExposure: <K extends keyof ISupplierExposure>(
      _property: K,
      _value: ISupplierExposure[K],
      _index: number,
    ) => void;
    updateSingleSupplierExposureCalendar: (_materialName: string, _newExposureCalendar: MonthYearValue[]) => void;
  };
  showAdjustments?: boolean;
  materialIndexChanged?: (_index: string, _matId: string) => void;
}
const dummy = () => null;
export const EditFutureOrderTable: React.FC<EditFutureOrderTableProps> = ({
  showAdjustments = true,
  materialIndexChanged = dummy,
  futureOrderData,
  materialsMap,
}) => {
  const { editedFutureOrder, updateSingleSupplierExposureCalendar, updateSupplierExposure } = futureOrderData;
  const { supplierExposure } = editedFutureOrder;
  const { formulaColorDictionary, account } = useAccountStore();
  const [materialIndexMap, setMaterialIndexMap] = React.useState<Record<string, IClientExposure>>(
    editedFutureOrder.clientExposure,
  );

  const { pricesMap } = useCurrencyRates();

  const rawMaterialExposureToTableData = (
    editedFutureOrderData: ISupplierExposure[],
    priceMap: Record<string, number>,
  ): MaterialData[] => {
    const materialData: MaterialData[] = editedFutureOrderData.map(
      ({ formulaId, basePrice, weightInProduct, supplyLeadTime, adjustment }, index) => ({
        basePrice,
        formulaId,
        weightInProduct,
        material: materialsMap[formulaId].name,
        materialColor: formulaColorDictionary[formulaId],
        priceIndex: materialsMap[formulaId].priceIndex,
        leadTime: {
          months: supplyLeadTime,
          setMonthsValue: (month: number) => {
            updateSupplierExposure('supplyLeadTime', month, index); // updates Supplier exposure
            const functionToGetNewExposureTable =
              RAW_MATERIALS_PRICE_ADJUSTMENT_DICTIONARY[editedFutureOrder.supplierExposure[index].adjustment];
            const newExposureTable = functionToGetNewExposureTable(
              -month,
              weightInProduct,
              editedFutureOrder.orderDetails.productDeliveryCalendar!,
            );
            updateSingleSupplierExposureCalendar(formulaId, newExposureTable);
          },
        },
        adjustment: {
          adjustmentType: adjustment,
          options: Object.keys(RAW_MATERIALS_PRICE_ADJUSTMENT_DICTIONARY).filter((k) => k !== 'BackToBack'),
          setAdjustment: (option: string) => {
            updateSupplierExposure('adjustment', option, index); // Update supplier exposure
            const functionToGetNewExposureTable = RAW_MATERIALS_PRICE_ADJUSTMENT_DICTIONARY[option];
            const newExposureTable = functionToGetNewExposureTable(
              -supplyLeadTime,
              weightInProduct,
              editedFutureOrder.orderDetails.productDeliveryCalendar!,
            );
            updateSingleSupplierExposureCalendar(formulaId, newExposureTable);
          },
        },
        quantity: weightInProduct * editedFutureOrder.orderDetails.quantity,
        totalPrice: weightInProduct * editedFutureOrder.orderDetails.quantity * priceMap[formulaId],
      }),
    );
    return materialData;
  };

  const columnHeaders = showAdjustments
    ? ['Material', 'Quantity', `Base price `, 'Total price', 'Supply Lead Time', 'Price Index', 'Adjustment']
    : ['Material', 'Weight per unit', 'Total Weight', 'Total price', 'Price Index'];

  return (
    <table className="materials-table">
      <thead>
        <tr>
          {columnHeaders.map((header, i) => (
            <th key={'h' + i}>{header}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rawMaterialExposureToTableData(supplierExposure, pricesMap)
          .map((item) => ({ ...item, unit: getUnitObj(materialsMap[item.formulaId]?.unit) }))
          .map((item, index) => (
            <tr key={index}>
              <td className="ps-0">
                <div className="ps-[0.5rem]" style={{ borderLeft: `6px solid ${item.materialColor}` }}>
                  {item.material}
                </div>
              </td>
              {!showAdjustments && (
                <td>
                  {formatToShortString(item.weightInProduct, 0)} {item.unit.shortForm}
                </td>
              )}
              <td>
                {formatToShortString(item.quantity, 0)} {item.unit.shortForm}
              </td>
              {showAdjustments && <td>${formatToShortString(pricesMap[item.formulaId], 0)} USD</td>}
              <td>${formatToShortString(item.totalPrice, 0)} USD</td>
              {showAdjustments && (
                <td>
                  <div className="w-[10rem]">
                    <ToggleNumberInput
                      valueState={item.leadTime.months}
                      setValueState={item.leadTime.setMonthsValue}
                      downLimit={0}
                      upperLimit={11}
                      suffix="m"
                    />
                  </div>
                </td>
              )}
              <td>
                {showAdjustments ? (
                  item.priceIndex
                ) : (
                  <div className="w-52">
                    <OptionSelect
                      currentOption={materialIndexMap[item.formulaId]?.priceIndex || defaultAdjustment}
                      options={account.AdjustmentsInfo.productPriceIndexes}
                      setCurrentOption={(priceIndex: string) => {
                        setMaterialIndexMap((currMap) => ({
                          ...currMap,
                          [item.formulaId]: { ...currMap[item.formulaId], priceIndex },
                        }));
                        materialIndexChanged(priceIndex, item.formulaId);
                      }}
                    />
                  </div>
                )}
              </td>
              {showAdjustments && (
                <td>
                  <div className="max-w-[12rem]">
                    <OptionSelect
                      currentOption={item.adjustment.adjustmentType}
                      options={item.adjustment.options}
                      setCurrentOption={item.adjustment.setAdjustment}
                      subtitle=""
                    />
                  </div>
                </td>
              )}
            </tr>
          ))}
      </tbody>
    </table>
  );
};

const getSupplierMinusClient = (
  // [Recheck - if not used delete]
  editedOrder: IFutureOrder,
): { formulaId: string; exposureCalendar: MonthYearValue[] }[] => {
  const totalExposures = calcSupplierMinusClient(editedOrder.supplierExposure, editedOrder.clientExposure);

  const accumulatedExposures = accumulateExposures(totalExposures);

  const supplierMinusClient = Object.entries(accumulatedExposures).map(([formulaId, exposureCalendar]) => ({
    formulaId,
    exposureCalendar,
  }));

  return supplierMinusClient;
};
